import React from 'react';
import { Link } from 'react-router-dom';

/**
 * AssessmentResultsListHeader
 *
 * Simple header used on the AssessmentResults list page.
 */
const AssessmentResultsListHeader = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Assessment Results
        </h1>
        <Link
          to="/"
          className="text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 transition-colors"
        >
          ← Back to Home
        </Link>
      </div>
      <p className="text-gray-600 dark:text-gray-400">
        View completed assessments and generate professional executive reports.
      </p>
    </div>
  );
};

/**
 * Header component for enhanced assessment results
 * Optimized for consultant screen sharing
 */
export const AssessmentResultsHeader = ({ assessment, assessmentId, isUser }) => {
  return (
    <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-emerald-800 text-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">RA</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold">Rely Ability</h1>
                <p className="text-emerald-200">Energy Productivity Solutions</p>
              </div>
            </div>

            <h2 className="text-4xl font-bold mb-2">Energy Savings Assessment</h2>
            <h3 className="text-xl text-emerald-200 mb-4">Professional Analysis Report</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-emerald-200">Facility:</span>
                <div className="font-medium text-lg">{assessment?.facility_name || 'Assessment Results'}</div>
              </div>
              <div>
                <span className="text-emerald-200">Industry:</span>
                <div className="font-medium text-lg">{assessment?.industry_name || 'Manufacturing'}</div>
              </div>
              <div>
                <span className="text-emerald-200">Analysis Type:</span>
                <div className="font-medium text-lg">Consultant-Led Professional Assessment</div>
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="bg-emerald-500/20 border border-emerald-400/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="font-medium text-emerald-300">Consultant-Led Session</span>
              </div>
              <p className="text-sm text-emerald-200">
                Live professional assessment with expert guidance
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="block text-emerald-200 hover:text-white transition-colors text-lg"
              >
                ← Back to Home
              </Link>
              {isUser && (
                <Link
                  to={`/assessments/${assessmentId}/recommendations`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  View Recommendations
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Professional Analysis Badge */}
        <div className="bg-emerald-600/20 border border-emerald-400/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-emerald-300">✓</span>
            <span className="font-medium text-lg">Professional IAC Database Analysis with Real Implementation Data</span>
          </div>
          <p className="text-emerald-200">
            This report provides detailed implementation guides, ARC specifications, and comprehensive benchmarking data from real industrial assessments.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AssessmentResultsListHeader;

